import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Col, Form, InputGroup, Modal, Pagination, Row, Table } from "react-bootstrap";
import { FaDownload, FaEye, FaFilter, FaSearch, FaTrash, FaUpload } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { empleadosService } from "../services/api";

const PAGE_SIZE = 8;

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const renderDocumentStatusBadge = (code) => {
  switch (code) {
    case 0:
      return { label: "Válido", variant: "success" };
    case 2:
      return { label: "Sin Presentar", variant: "secondary" };
    case 3:
      return { label: "Caducidad Próxima", variant: "warning" };
    case 4:
      return { label: "Caducado", variant: "danger" };
    default:
      return { label: "Sin Estado", variant: "light" };
  }
};

const DocumentacionList = () => {
  const navigate = useNavigate();

  // Estado de filtros
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [page, setPage] = useState(1);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("-1");
  const [documentStatusFilter, setDocumentStatusFilter] = useState("-1");
  const [selectedRelacionesLaborales, setSelectedRelacionesLaborales] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedDepartamento, setSelectedDepartamento] = useState("-1");
  const [activoFilter, setActivoFilter] = useState("-1");

  // Catálogos para los multiselect / select
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [relacionesLaborales, setRelacionesLaborales] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [showDocumentsDropdown, setShowDocumentsDropdown] = useState(false);
  const [showRelacionesLaboralesDropdown, setShowRelacionesLaboralesDropdown] = useState(false);

  // Datos de la página actual
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });

  // Modal de subida
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validityDate, setValidityDate] = useState("");
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Modal de borrado
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [deletingFile, setDeletingFile] = useState(false);

  const loadFilterCatalogs = async () => {
    try {
      const [docsRes, relRes, deptoRes] = await Promise.all([
        empleadosService.getDocumentTypes(),
        empleadosService.getRelacionLaboral(null),
        empleadosService.getDepartamentos(null),
      ]);
      setTiposDocumentos(docsRes.data?.documentTypes || []);
      setRelacionesLaborales(relRes.data?.relacionesLaborales || []);
      setDepartamentos(deptoRes.data?.departamentos || []);
    } catch (error) {
      setTiposDocumentos([]);
      setRelacionesLaborales([]);
      setDepartamentos([]);
      toast.error("No se pudieron cargar los filtros de documentación");
    }
  };

  const loadDocumentos = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchApplied,
        page,
        limit: PAGE_SIZE,
        orderBy: 0,
        statusFilter,
        documentStatusFilter,
        selectedRelacionesLaborales,
        selectedDocuments,
        selectedDepartamento,
        activoFilter,
      };

      const res = await empleadosService.getDocumentosCentralizados(params);
      setRows(res.data?.documentos || []);
      setPagination(
        res.data?.pagination || {
          page: 1,
          limit: PAGE_SIZE,
          total: 0,
          pages: 1,
        }
      );
    } catch (error) {
      setRows([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
      toast.error(error.response?.data?.message || "Error al cargar la documentación centralizada");
    } finally {
      setLoading(false);
    }
  };

  /** Resuelve el tipo de documento: catálogo (prioridad) o campos de la fila del listado. */
  const getTipoFromRow = (row) => {
    if (!row?.DOCUMENTO_TIPO_ID) return null;
    const fromCat = tiposDocumentos.find((t) => String(t.ID) === String(row.DOCUMENTO_TIPO_ID));
    if (fromCat) return fromCat;
    return {
      ID: row.DOCUMENTO_TIPO_ID,
      NOMBRE: row.NOMBRE_TIPO_DOCUMENTO,
      DOCUMENTO_SIN_RENOVACION: row.DOCUMENTO_SIN_RENOVACION,
      MESES_HASTA_PROX_RENOVACION: row.MESES_HASTA_PROX_RENOVACION,
    };
  };

  const getTipoDocumentoForSelectedRow = () => getTipoFromRow(selectedRow);

  // Abre el modal de subida
  const openUploadModal = (row) => {
    setSelectedRow(row);
    setSelectedFile(null);

    const tipo = getTipoFromRow(row);
    const sinRenovacion = tipo && (tipo.DOCUMENTO_SIN_RENOVACION === true || tipo.DOCUMENTO_SIN_RENOVACION === 1);

    if (sinRenovacion) {
      setValidityDate(null);
    } else if (tipo && tipo.MESES_HASTA_PROX_RENOVACION !== null && tipo.MESES_HASTA_PROX_RENOVACION !== undefined) {
      const fechaActual = new Date();
      const fechaValidez = new Date(fechaActual);
      fechaValidez.setMonth(fechaValidez.getMonth() + Number(tipo.MESES_HASTA_PROX_RENOVACION));
      setValidityDate(fechaValidez.toISOString().split("T")[0]);
    } else {
      setValidityDate("");
    }

    setShowUploadModal(true);
  };

  // Selección de fichero
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRow) return;

    if (file.type !== "application/pdf") {
      toast.error("Solo se permiten archivos PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo no puede ser mayor a 10MB");
      return;
    }

    setSelectedFile(file);

    const tipo = getTipoFromRow(selectedRow);
    const esSinRenovacion = tipo && (tipo.DOCUMENTO_SIN_RENOVACION === true || tipo.DOCUMENTO_SIN_RENOVACION === 1);

    if (esSinRenovacion) {
      setValidityDate(null);
    } else if (tipo && tipo.MESES_HASTA_PROX_RENOVACION !== null && tipo.MESES_HASTA_PROX_RENOVACION !== undefined) {
      const fechaActual = new Date();
      const fechaValidez = new Date(fechaActual);
      fechaValidez.setMonth(fechaValidez.getMonth() + tipo.MESES_HASTA_PROX_RENOVACION);
      setValidityDate(fechaValidez.toISOString().split("T")[0]);
    } else {
      const fechaFormateada = new Date().toISOString().split("T")[0];
      setValidityDate(fechaFormateada);
    }
  };

  // Sube documento
  const handleUploadFile = async () => {
    if (!selectedRow) return;
    if (!selectedFile) {
      toast.error("Por favor selecciona un archivo");
      return;
    }

    const tipoDocumento = getTipoDocumentoForSelectedRow();
    const esDocumentoSinRenovacion =
      tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

    if (!esDocumentoSinRenovacion) {
      if (!validityDate) {
        toast.error("Por favor selecciona una fecha de validez");
        return;
      }
      const fechaValidez = new Date(validityDate);
      const fechaActual = new Date();
      fechaActual.setHours(0, 0, 0, 0);
      if (fechaValidez < fechaActual) {
        toast.error("La fecha de validez no puede ser inferior a la fecha actual");
        return;
      }
    }

    try {
      setUploadingDocument(true);
      const fechaAEnviar = esDocumentoSinRenovacion ? null : validityDate || null;
      const response = await empleadosService.uploadDocument(selectedRow.EMPLEADO_PRL_ID, selectedRow.DOCUMENTO_TIPO_ID, selectedFile, fechaAEnviar);

      if (response.data?.success) {
        toast.success("Documento subido correctamente con fecha de validez");
        setShowUploadModal(false);
        setSelectedRow(null);
        setSelectedFile(null);
        setValidityDate(null);
        await loadDocumentos();
      } else {
        toast.error(response.data?.message || "No se pudo subir el documento");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error subiendo documento");
    } finally {
      setUploadingDocument(false);
    }
  };

  // Descarga el fichero del documento
  const handleDownloadDocument = async (row) => {
    if (!row?.FICHERO) {
      toast.warning("No hay fichero para descargar");
      return;
    }
    try {
      const response = await empleadosService.downloadDocumentFile(
        row.EMPLEADO_PRL_ID,
        row.DOCUMENTO_TIPO_ID,
      );
      const nombreBase = (row.NOMBRE_TIPO_DOCUMENTO || "documento")
        .replace(/[<>:"/\\|?*]/g, "_")
        .trim();
      const fileName = nombreBase.toLowerCase().endsWith(".pdf")
        ? nombreBase
        : `${nombreBase}.pdf`;
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Descarga completada");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error al descargar el documento");
    }
  };

  // Navega al detalle del empleado
  const handleVerDetalleEmpleado = (row) => {
    if (!row?.EMPLEADO_PRL_ID) return;
    navigate(`/empleado-detail?empleado_id=${row.EMPLEADO_PRL_ID}`);
  };

  // Abre el modal de confirmación de borrado
  const requestDeleteDocument = (row) => {
    if (!row?.FICHERO) {
      toast.info("Este documento no tiene fichero para eliminar");
      return;
    }
    setRowPendingDelete(row);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingFile) return;
    setShowDeleteModal(false);
    setRowPendingDelete(null);
  };

  // Ejecuta la eliminación tras confirmar en el modal
  const confirmDeleteDocument = async () => {
    const row = rowPendingDelete;
    if (!row?.FICHERO) {
      closeDeleteModal();
      return;
    }
    try {
      setDeletingFile(true);
      const response = await empleadosService.deleteDocumentFile(row.EMPLEADO_PRL_ID, row.DOCUMENTO_TIPO_ID);
      if (response.data?.success) {
        toast.success("Fichero eliminado correctamente");
        setShowDeleteModal(false);
        setRowPendingDelete(null);
        await loadDocumentos();
      } else {
        toast.error(response.data?.message || "No se pudo eliminar el fichero");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error eliminando fichero");
    } finally {
      setDeletingFile(false);
    }
  };

  // Resetea los filtros a su estado inicial
  const resetFilters = () => {
    setSearchInput("");
    setSearchApplied("");
    setStatusFilter("-1");
    setDocumentStatusFilter("-1");
    setSelectedRelacionesLaborales([]);
    setSelectedDocuments([]);
    setSelectedDepartamento("-1");
    setActivoFilter("-1");
    setPage(1);
  };

  // Catálogos
  useEffect(() => {
    loadFilterCatalogs();
  }, []);

  // Recarga ante cualquier cambio de filtro/paginación
  useEffect(() => {
    loadDocumentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchApplied, statusFilter, documentStatusFilter, selectedRelacionesLaborales, selectedDocuments, selectedDepartamento, activoFilter]);

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Documentación</h4>
        <Badge bg="secondary">{`${pagination.total} documento(s)`}</Badge>
      </Card.Header>

      <Card.Body>
        {/* Buscador + toggle de filtros avanzados */}
        <Form
          className="mb-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSearchApplied(searchInput);
            setPage(1);
          }}
        >
          <div className="d-flex gap-2 align-items-start">
            <InputGroup style={{ flex: 1 }}>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, email, teléfono, DNI o documento..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Button type="submit" variant="primary">
                <FaSearch />
              </Button>
            </InputGroup>
            <Button variant="outline-secondary" className="d-flex align-items-center gap-2" onClick={() => setShowAdvancedFilters((prev) => !prev)}>
              <FaFilter />
              Filtros {showAdvancedFilters ? "▼" : "▶"}
            </Button>
          </div>
        </Form>

        {/* Filtros avanzados */}
        {showAdvancedFilters && (
          <div className="mb-3 p-3 border rounded bg-light">
            <h6 className="mb-3">Filtros Avanzados</h6>

            <Row className="g-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Estado de Validación</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos los Estados</option>
                    <option value="1">Validado</option>
                    <option value="2">No Validado</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Activo</Form.Label>
                  <Form.Select
                    value={activoFilter}
                    onChange={(e) => {
                      setActivoFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos</option>
                    <option value="1">Sí</option>
                    <option value="0">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Estado de Documentos</Form.Label>
                  <Form.Select
                    value={documentStatusFilter}
                    onChange={(e) => {
                      setDocumentStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos</option>
                    <option value="0">Válidos</option>
                    <option value="1">No Válidos</option>
                    <option value="2">Sin Presentar</option>
                    <option value="3">Caducidad Próxima</option>
                    <option value="4">Caducados</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Departamento</Form.Label>
                  <Form.Select
                    value={selectedDepartamento}
                    onChange={(e) => {
                      setSelectedDepartamento(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos los Departamentos</option>
                    {departamentos.map((depto) => (
                      <option key={depto.ID} value={depto.ID}>
                        {depto.NOMBRE}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tipos de Documento</Form.Label>
                  <div className="position-relative">
                    <Button
                      variant="outline-secondary"
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => setShowDocumentsDropdown((prev) => !prev)}
                    >
                      <span className="text-truncate">
                        {selectedDocuments.length === 0 ? "Selecciona documentos..." : `${selectedDocuments.length} seleccionado(s)`}
                      </span>
                      <FontAwesomeIcon icon={showDocumentsDropdown ? faChevronUp : faChevronDown} />
                    </Button>
                    {showDocumentsDropdown && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000, maxHeight: "240px", overflowY: "auto" }}
                      >
                        {tiposDocumentos.map((documento) => {
                          const documentId = documento.ID || documento.id;
                          const isSelected = selectedDocuments.includes(documentId);
                          return (
                            <div
                              key={documentId}
                              className="px-3 py-2 d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setPage(1);
                                setSelectedDocuments((prev) => (isSelected ? prev.filter((id) => id !== documentId) : [...prev, documentId]));
                              }}
                            >
                              <Form.Check checked={isSelected} readOnly className="me-2" />
                              <span>{documento.NOMBRE || documento.nombre}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Relación Laboral</Form.Label>
                  <div className="position-relative">
                    <Button
                      variant="outline-secondary"
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => setShowRelacionesLaboralesDropdown((prev) => !prev)}
                    >
                      <span className="text-truncate">
                        {selectedRelacionesLaborales.length === 0
                          ? "Selecciona relaciones laborales..."
                          : `${selectedRelacionesLaborales.length} seleccionado(s)`}
                      </span>
                      <FontAwesomeIcon icon={showRelacionesLaboralesDropdown ? faChevronUp : faChevronDown} />
                    </Button>
                    {showRelacionesLaboralesDropdown && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000, maxHeight: "240px", overflowY: "auto" }}
                      >
                        {relacionesLaborales.map((relacion) => {
                          const relacionId = relacion.ID || relacion.id;
                          const isSelected = selectedRelacionesLaborales.includes(relacionId);
                          return (
                            <div
                              key={relacionId}
                              className="px-3 py-2 d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setPage(1);
                                setSelectedRelacionesLaborales((prev) =>
                                  isSelected ? prev.filter((id) => id !== relacionId) : [...prev, relacionId]
                                );
                              }}
                            >
                              <Form.Check checked={isSelected} readOnly className="me-2" />
                              <span>{relacion.TEXTO || relacion.texto}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-end justify-content-end">
                <Button variant="outline-primary" onClick={resetFilters}>
                  Limpiar Filtros
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {/* Tabla centralizada */}
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>EMPLEADO</th>
              <th>EMAIL</th>
              <th>TELÉFONO</th>
              <th>DNI</th>
              <th>DOCUMENTO</th>
              <th>ESTADO</th>
              <th>FECHA VALIDEZ</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  Cargando documentación...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center">
                  No hay documentos para los filtros seleccionados
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const status = renderDocumentStatusBadge(row.DOCUMENT_STATUS);
                return (
                  <tr key={row.DOCUMENTO_ID}>
                    <td>{row.EMPLEADO}</td>
                    <td>{row.EMAIL || "-"}</td>
                    <td>{row.TELEFONO || "-"}</td>
                    <td>{row.DNI || "-"}</td>
                    <td>{row.NOMBRE_TIPO_DOCUMENTO}</td>
                    <td>
                      <Badge bg={status.variant}>{status.label}</Badge>
                    </td>
                    <td>{formatDate(row.HASTA)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        <Button variant="outline-info" size="sm" title="Ver detalle del empleado" onClick={() => handleVerDetalleEmpleado(row)}>
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Descargar documento"
                          disabled={!row.FICHERO}
                          onClick={() => handleDownloadDocument(row)}
                        >
                          <FaDownload />
                        </Button>
                        <Button variant="outline-primary" size="sm" title="Subir/Actualizar documento" onClick={() => openUploadModal(row)}>
                          <FaUpload />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Eliminar fichero"
                          disabled={!row.FICHERO}
                          onClick={() => requestDeleteDocument(row)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.First onClick={() => setPage(1)} disabled={pagination.page === 1} />
              <Pagination.Prev onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page === 1} />
              {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                .filter(
                  (pageNum) => pageNum === 1 || pageNum === pagination.pages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                )
                .map((pageNum, idx, arr) => {
                  if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${pageNum}`}>
                        <Pagination.Ellipsis disabled />
                        <Pagination.Item active={pageNum === pagination.page} onClick={() => setPage(pageNum)}>
                          {pageNum}
                        </Pagination.Item>
                      </React.Fragment>
                    );
                  }
                  return (
                    <Pagination.Item key={pageNum} active={pageNum === pagination.page} onClick={() => setPage(pageNum)}>
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
              <Pagination.Next onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page === pagination.pages} />
              <Pagination.Last onClick={() => setPage(pagination.pages)} disabled={pagination.page === pagination.pages} />
            </Pagination>
          </div>
        )}
      </Card.Body>

      {/* Confirmación eliminar fichero */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar fichero</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rowPendingDelete && (
            <>
              <p className="mb-2">¿Seguro que quieres eliminar el fichero de este documento? Esta acción no se puede deshacer.</p>
              <ul className="mb-0 small text-muted">
                <li>
                  <strong>Empleado:</strong> {rowPendingDelete.EMPLEADO}
                </li>
                <li>
                  <strong>Documento:</strong> {rowPendingDelete.NOMBRE_TIPO_DOCUMENTO}
                </li>
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deletingFile}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDeleteDocument} disabled={deletingFile}>
            {deletingFile ? "Eliminando..." : "Eliminar fichero"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal subir documento */}
      <Modal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
          setValidityDate("");
          setSelectedRow(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Subir Documento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRow?.EMPLEADO && (
            <p className="text-muted small mb-3">
              <strong>Empleado:</strong> {selectedRow.EMPLEADO}
            </p>
          )}

          {selectedRow?.DOCUMENTO_TIPO_ID &&
            (() => {
              const tipoDocumento = getTipoDocumentoForSelectedRow();
              if (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1) {
                return (
                  <div className="alert alert-warning mb-3">
                    <small>
                      <strong>Información:</strong> Este documento no tiene fecha de caducidad.
                      <br />
                      <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                    </small>
                  </div>
                );
              }
              if (tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                return (
                  <div className="alert alert-info mb-3">
                    <small>
                      <strong>Información:</strong> La fecha de validez se calculará automáticamente sumando{" "}
                      {tipoDocumento.MESES_HASTA_PROX_RENOVACION} mes(es) a la fecha actual.
                      <br />
                      <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                    </small>
                  </div>
                );
              }
              return (
                <div className="alert alert-warning mb-3">
                  <small>
                    <strong>Información:</strong> No hemos podido encontrar una fecha de Validez apta para el calculo de la fecha.
                    <br />
                    <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                  </small>
                </div>
              );
            })()}

          <div className="mb-3">
            <label className="form-label">Seleccionar archivo PDF:</label>
            <input type="file" className="form-control" accept=".pdf" onChange={handleFileSelect} disabled={uploadingDocument} />
            <small className="text-muted">Solo archivos PDF, máximo 10MB</small>
          </div>

          {selectedFile && (
            <div className="alert alert-info">
              <strong>Archivo seleccionado:</strong> {selectedFile.name}
              <br />
              <strong>Tamaño:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </div>
          )}

          {selectedFile &&
            (() => {
              const tipoDocumento = getTipoDocumentoForSelectedRow();
              const esDocumentoSinRenovacion =
                tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

              return (
                <div className="alert alert-info">
                  <h6>{esDocumentoSinRenovacion ? "Información del documento:" : "Se ha detectado la Validez del documento hasta:"}</h6>

                  {!esDocumentoSinRenovacion ? (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Fecha de Caducidad:
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={validityDate ?? ""}
                        onChange={(e) => setValidityDate(e.target.value)}
                        required
                        isInvalid={!validityDate}
                      />
                      <Form.Control.Feedback type="invalid">Por favor selecciona una fecha de validez</Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {tipoDocumento &&
                        tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null &&
                        tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined
                          ? "Puedes modificar la fecha calculada automáticamente si es necesario."
                          : "La fecha mostrada es la fecha actual. Puedes modificarla según sea necesario."}
                      </Form.Text>
                    </Form.Group>
                  ) : (
                    <div className="alert alert-success mb-3">
                      <small>
                        <strong>Información:</strong> Este documento no requiere fecha de caducidad.
                        <br />
                        Se subirá sin fecha de validez.
                      </small>
                    </div>
                  )}
                </div>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          {(() => {
            const tipoDocumento = getTipoDocumentoForSelectedRow();
            const esDocumentoSinRenovacion =
              tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

            return (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setValidityDate(null);
                    setSelectedRow(null);
                  }}
                  disabled={uploadingDocument}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleUploadFile}
                  disabled={!selectedFile || (!validityDate && !esDocumentoSinRenovacion) || uploadingDocument}
                >
                  {uploadingDocument ? "Subiendo..." : "Subir Documento y Guardar Fecha"}
                </Button>
              </>
            );
          })()}
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default DocumentacionList;
